import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { MobxComponent } from '../../mobx/components';

interface Props {
	menuId?: string;
	scope?: 'current-menu';
	entityId?: string;
	emptyText?: string;
}

/**
 * Deterministic colour per person, so the same face keeps the same colour
 * between sessions and across everyone's screen. Hashing the id rather than
 * assigning by position matters: position changes as people come and go.
 */
const AVATAR_COLOURS = ['#3b6ea5', '#7a4fa3', '#1f7a44', '#a35a1f', '#a12727', '#1f6f7a'];

export function colourFor(userId: string): string {
	let hash = 0;
	for (let i = 0; i < userId.length; i++) {
		hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
	}
	return AVATAR_COLOURS[hash % AVATAR_COLOURS.length];
}

export function initialsFor(name: string): string {
	const parts = name.trim().split(/[\s@._-]+/).filter(Boolean);
	if (parts.length === 0) return '?';
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[1][0]).toUpperCase();
}

const ENTITY_NOUN: Record<MenuPresenceEntityType, string> = {
	menu: 'menu settings',
	category: 'category',
	item: 'item',
	modifier: 'modifier',
};

function describe(member: MenuPresenceMember, sameEntity: boolean): string {
	if (member.entityType) {
		const noun = ENTITY_NOUN[member.entityType];
		const name = member.entityName ? ` ${member.entityName}` : '';
		const here = sameEntity ? ' — the one you have open' : '';
		return `${member.name} is editing the ${noun}${name}${here}`;
	}
	if (member.menuId) return `${member.name} is viewing this menu`;
	return `${member.name} is viewing the menu list`;
}

const MAX_FACES = 4;

/**
 * Who else is on a menu screen right now.
 *
 * This is prevention before the fact: the version lock refuses a save that
 * would overwrite someone, which is late and irritating. Seeing that a
 * colleague is already in the menu is what stops the collision being created.
 */
@inject('store')
@observer
export class MenuPresence extends MobxComponent<Props, { listOpen: boolean }> {
	state = { listOpen: false };

	renderRoster(members: MenuPresenceMember[], entityId?: string) {
		return (
			<span
				style={{
					position: 'absolute',
					top: 'calc(100% + 6px)',
					right: 0,
					zIndex: 60,
					background: '#fff',
					border: '1px solid #e2e2e2',
					borderRadius: 6,
					boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
					padding: '8px 10px',
					minWidth: 240,
					display: 'block',
					cursor: 'default',
				}}
			>
				{members.map((member) => {
					const sameEntity = !!entityId && member.entityId === entityId;
					return (
						<span
							key={member.connectionId}
							style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0' }}
						>
							<span
								style={{
									width: 20,
									height: 20,
									borderRadius: '50%',
									background: colourFor(member.userId || member.name),
									color: '#fff',
									fontSize: 9,
									fontWeight: 600,
									display: 'inline-flex',
									alignItems: 'center',
									justifyContent: 'center',
									flexShrink: 0,
								}}
							>
								{initialsFor(member.name)}
							</span>
							<span style={{ fontSize: 12, color: sameEntity ? '#b02a2a' : '#444' }}>
								{describe(member, sameEntity)}
							</span>
						</span>
					);
				})}
			</span>
		);
	}

	render() {
		const { menuId, scope, entityId, emptyText } = this.props;
		const { store } = this.injected;

		const all = store.ably.menuPresence || [];
		const scopeMenuId = scope === 'current-menu' ? store.notifications.menuPresenceMenuId : menuId;
		const members = scopeMenuId ? all.filter(m => m.menuId === scopeMenuId) : all;

		if (members.length === 0) {
			if (!emptyText) return null;
			return <span style={{ fontSize: 12, color: '#aaa' }}>{emptyText}</span>;
		}

		const shown = members.slice(0, MAX_FACES);
		const overflow = members.length - shown.length;
		const editing = members.filter(m => m.activity === 'editing').length;
		const onSameThing = entityId ? members.filter(m => m.entityId === entityId).length : 0;

		return (
			<span
				style={{ display: 'inline-flex', alignItems: 'center', gap: 6, position: 'relative' }}
				onMouseEnter={() => this.setState({ listOpen: true })}
				onMouseLeave={() => this.setState({ listOpen: false })}
			>
				<span style={{ display: 'inline-flex' }}>
					{shown.map((member, index) => {
						const sameEntity = !!entityId && member.entityId === entityId;
						return (
						<span
							key={member.connectionId}
							title={describe(member, sameEntity)}
							style={{
								width: 24,
								height: 24,
								borderRadius: '50%',
								background: colourFor(member.userId || member.name),
								color: '#fff',
								fontSize: 10,
								fontWeight: 600,
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginLeft: index === 0 ? 0 : -6,
								boxShadow: '0 0 0 2px #fff',
								// Red for someone on the exact thing you have open, which
								// is the collision. Amber for someone in some other
								// editor, which is only worth noticing.
								border: sameEntity
									? '2px solid #d64545'
									: member.activity === 'editing'
										? '2px solid #e0a800'
										: '2px solid transparent',
								boxSizing: 'border-box',
							}}
						>
							{initialsFor(member.name)}
						</span>
						);
					})}
					{overflow > 0 && (
						<span
							title={members.slice(MAX_FACES).map(m => describe(m, !!entityId && m.entityId === entityId)).join('\n')}
							style={{
								width: 24,
								height: 24,
								borderRadius: '50%',
								background: '#e4e4e4',
								color: '#666',
								fontSize: 10,
								fontWeight: 600,
								display: 'inline-flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginLeft: -6,
								boxShadow: '0 0 0 2px #fff',
							}}
						>
							+{overflow}
						</span>
					)}
				</span>
				<span
					style={{
						fontSize: 12,
						color: onSameThing > 0 ? '#b02a2a' : editing > 0 ? '#8a6d00' : '#888',
						fontWeight: onSameThing > 0 ? 600 : 400,
					}}
				>
					{onSameThing > 0
						? `${onSameThing} on this screen`
						: editing > 0
							? `${editing} editing`
							: `${members.length} here`}
				</span>
				{this.state.listOpen && this.renderRoster(members, entityId)}
			</span>
		);
	}
}
